import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as service from "../service/MyTimesheetService";
import { setIsLoading, setMyTimesheetAllList, setTimesheetDetailsByEmp } from "../slices/myTimesheetSlice";
import { Alert, Modal } from "antd";
import { useTranslation } from "react-i18next";
import { generateRandomString } from "utils/helper";

const useMyTeam = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const [useFilter, setUseFilter] = useState(false);
    const [useFilterDetail, setUseFilterDetail] = useState(false);
    const { currentCompany, currentEmployee } = useSelector((state) => state.company);
    const [pagination, setPagination] = useState({ page: 0, size: 10 });
    const [paginationDetails, setPaginationDetails] = useState({ page: 0, size: 10 });
    const { isLoading, myTimesheetAllList, timesheetDetailsByEmp } = useSelector((state) => state.myTimesheet);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const hasSelected = selectedRowKeys.length > 0;
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [projectForApprover, setProjectForApprover] = useState([]);
    const [taskDropdown, setTaskDropdown] = useState([]);
    const [search, setSearch] = useState({});
    const [searchDetail, setSearchDetail] = useState({});
    const [myTimesheetAllListWithKey, setMyTimesheetAllListWithKey] = useState([]);

    const toParams = useCallback((params) => {
        const { pagination = {}, sortBy = [] } = params;
        const modifiedParams = {
            ...params,
            page: pagination.page || 0,
            size: pagination.pageSize || 10,
            sortBy: sortBy
                .filter((s) => s.order !== undefined)
                .map((s) => ({
                    direction: s.order === "descend" ? "desc" : "asc",
                    property: s.field,
                })),
        };
        return modifiedParams;

    }, [])

    const getTimesheetAllList = useCallback(async (params) => {
        try {
            dispatch(setIsLoading(true))
            const response = await service.getTimesheetPaginationForApprover({
                ...params,
                companyId: currentCompany,
                employeeId: currentEmployee,
            })

            dispatch(setMyTimesheetAllList(response?.data?.page?.content || []))
            setPagination({
                ...params?.pagination,
                current: response?.data?.page?.number + 1,
                pageNo: response?.data?.page?.number + 1,
                page: response?.data?.page?.number + 1,
                total: response.data?.page?.totalElements,
                size: response?.data?.page?.totalPages
            });
            dispatch(setIsLoading(false))
        } catch (error) {
            console.log("error ", error);
            dispatch(setIsLoading(false))
        }
    }, [currentCompany, currentEmployee])

    const onPageChange = useCallback(
        (tablePagination, tableSorter) => {

            const modifiedSorter = Array.isArray(tableSorter)
                ? [...tableSorter]
                : [tableSorter];

            const modifiedParams = toParams({
                pagination: {
                    page: tablePagination?.current - 1,
                    size: tablePagination?.pageSize,
                    pageSize: tablePagination?.pageSize,
                    current: tablePagination.current - 1,
                },
                sortBy: modifiedSorter,
            });
            getTimesheetAllList(modifiedParams);
        },
        [getTimesheetAllList]
    );

    const onSelectChange = newSelectedRowKeys => {
        setSelectedRowKeys(newSelectedRowKeys);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
        getCheckboxProps: (record) => ({
            disabled: record?.approveStatusEn?.toLowerCase() === "complete"
        }),
    };

    useEffect(() => {
        if (currentCompany && currentEmployee) {
            getTimesheetAllList({ page: 0, size: 10 });
        }
    }, [currentEmployee, currentCompany])

    const approveTimesheetByEmployee = useCallback(
        async (record) => {
            try {
                dispatch(setIsLoading(true));
                const requestBody = {
                    companyId: currentCompany,
                    actionId: currentEmployee,
                    employeeId: currentEmployee,
                    sendingType: "APPROVED",
                    txnMyTimeSheetApproveListId: record.map((r) => r.myTimeSheetApproveId).flat(),
                };
                await service.approveEmployeeTimeSheet(requestBody);
            } catch (error) {
                Alert({ type: "error", resultObject: error });
            } finally {
                dispatch(setIsLoading(false));
                getTimesheetAllList({ page: 0, size: 10 });
            }
        },
        [dispatch, currentCompany, currentEmployee, getTimesheetAllList]
    );

    const rejectTimesheetByEmployee = useCallback(
        async (record) => {
            try {
                dispatch(setIsLoading(true));
                const requestBody = {
                    companyId: currentCompany,
                    actionId: currentEmployee,
                    employeeId: currentEmployee,
                    sendingType: "REJECT",
                    txnMyTimeSheetApproveListId: record.map((r) => r.myTimeSheetApproveId).flat(),
                };
                await service.approveEmployeeTimeSheet(requestBody);
            } catch (error) {
                Alert({ type: "error", resultObject: error });
            } finally {
                dispatch(setIsLoading(false));
                getTimesheetAllList({ page: 0, size: 10 });
            }
        },
        [dispatch, currentCompany, currentEmployee, getTimesheetAllList]
    );

    const onApproveTimesheetByEmployee = useCallback(
        (selectedRecords) => {
            return new Promise((resolve) => {

                Modal.confirm({
                    title: t("my_timesheet.label.confirm_send_approve"),
                    content: (
                        <span>
                            {i18n.language == "en" ? `Are you sure you want to confirm the timesheet approval ?`
                                : `คุณแน่ใจหรือไม่ที่จะยืนยันการอนุมัติไทม์ชีต`}
                        </span>
                    ),
                    onOk() {
                        return approveTimesheetByEmployee(selectedRecords).then(resolve);
                    },
                    okText: t('common.confirm'),
                    cancelText: t('common.cancel'),
                    okButtonProps: { style: { height: "40px", borderRadius: "8px", backgroundColor: "var(--button-color)", boxShadow: "none" } },
                    cancelButtonProps: { style: { height: "40px", borderRadius: "8px", boxShadow: "none" } },
                });
            });
        },
        [approveTimesheetByEmployee, i18n.language, t]
    );

    const onRejectedTimesheetByEmployee = useCallback(
        (selectedRecords) => {
            return new Promise((resolve) => {

                Modal.confirm({
                    title: t("my_timesheet.label.confirm_send_approve"),
                    content: (
                        <span>
                            {i18n.language == "en" ? `Are you sure you want to confirm disapproval of timesheet ?`
                                : `คุณแน่ใจหรือไม่ที่จะยืนยันการไม่อนุมัติไทม์ชีต`}
                        </span>
                    ),
                    onOk() {
                        return rejectTimesheetByEmployee(selectedRecords).then(resolve);
                    },
                    okText: t('common.confirm'),
                    cancelText: t('common.cancel'),
                    okButtonProps: { style: { height: "40px", borderRadius: "8px", backgroundColor: "var(--button-color)", boxShadow: "none" } },
                    cancelButtonProps: { style: { height: "40px", borderRadius: "8px", boxShadow: "none" } },
                });
            });
        },
        [rejectTimesheetByEmployee, i18n.language, t]
    );

    const getTimesheetDetails = useCallback(async (pagination, timesheetId, month, params) => {
        try {
            dispatch(setIsLoading(true))
            const response = await service.getTimesheetDetailsByEmployeeId({
                ...pagination,
                ...params,
                companyId: currentCompany,
                employeeId: params?.employeeId,
                projectId: timesheetId,
                month: month,
            })

            dispatch(setTimesheetDetailsByEmp(response?.data?.page?.content || []))
            setPaginationDetails({
                ...pagination,
                current: response?.data?.page?.number + 1,
                pageNo: response?.data?.page?.number + 1,
                page: response?.data?.page?.number + 1,
                total: response.data?.page?.totalElements,
                size: response?.data?.page?.totalPages
            });
            dispatch(setIsLoading(false))
        } catch (error) {
            console.log("error ", error);
            dispatch(setIsLoading(false))
        }
    }, [currentCompany, currentEmployee])

    const getProjectDropdownTask = useCallback(async (stpTimeSheetId) => {
        if (typeof currentCompany !== "object") {
            try {
                dispatch(setIsLoading(true));
                const values = {
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    employeeId: currentEmployee,
                    stpTimeSheetId: stpTimeSheetId
                };
                const response = await service.dropdownTaskByStpTimeSheetId(values);
                setTaskDropdown(response?.data?.content);
            } catch (error) {
            } finally {
                dispatch(setIsLoading(false));
            }
        }
    }, [currentCompany, currentEmployee, dispatch]);

    const onView = useCallback((record) => {
        setSelectedRecord(record);
        setIsModalOpen(true);
        getTimesheetDetails({ page: 0, size: 10 }, record?.stpTimeSheetId, record?.monthSubmit, { employeeId: record?.employeeId });
        getProjectDropdownTask(record?.stpTimeSheetId);
    }, [getTimesheetDetails, getProjectDropdownTask]);

    const onTimesheetDetailsPageChange = useCallback(
        (tablePagination, tableSorter) => {

            const modifiedSorter = Array.isArray(tableSorter)
                ? [...tableSorter]
                : [tableSorter];

            const modifiedParams = toParams({
                pagination: {
                    page: tablePagination?.current - 1,
                    size: tablePagination?.pageSize,
                    pageSize: tablePagination?.pageSize,
                    current: tablePagination.current - 1,
                },
                sortBy: modifiedSorter,
            });
            getTimesheetDetails(modifiedParams);
        },
        [getTimesheetDetails]
    );

    const approveTimesheetByList = useCallback(
        async (record) => {
            try {
                dispatch(setIsLoading(true));
                const requestBody = {
                    companyId: currentCompany,
                    actionId: currentEmployee,
                    employeeId: currentEmployee,
                    sendingType: "APPROVED",
                    txnMyTimeSheetList: record.map((r) => ({
                        myTimeSheetApproveId: r.myTimeSheetApproveId,
                        myTimeSheetListId: r.myTimeSheetListId,
                    }))

                };
                await service.approveEmployeeTimeSheetByList(requestBody);
            } catch (error) {
                Alert({ type: "error", resultObject: error });
            } finally {
                dispatch(setIsLoading(false));
                if (selectedRecord) {
                    getTimesheetDetails(
                        { page: 0, size: 10 },
                        selectedRecord?.stpTimeSheetId,
                        selectedRecord?.monthSubmit,
                        { employeeId: selectedRecord?.employeeId }
                    );
                }
                getTimesheetAllList({ page: 0, size: 10 });
            }
        },
        [dispatch, currentCompany, currentEmployee, selectedRecord, getTimesheetAllList, getTimesheetDetails]
    );
    const rejectTimesheetByList = useCallback(
        async (record) => {
            try {
                dispatch(setIsLoading(true));
                const requestBody = {
                    companyId: currentCompany,
                    actionId: currentEmployee,
                    employeeId: currentEmployee,
                    sendingType: "REJECT",
                    txnMyTimeSheetList: record.map((r) => ({
                        myTimeSheetApproveId: r.myTimeSheetApproveId,
                        myTimeSheetListId: r.myTimeSheetListId,
                    }))

                };
                await service.approveEmployeeTimeSheetByList(requestBody);
            } catch (error) {
                Alert({ type: "error", resultObject: error });
            } finally {
                dispatch(setIsLoading(false));
                if (selectedRecord) {
                    getTimesheetDetails(
                        { page: 0, size: 10 },
                        selectedRecord?.stpTimeSheetId,
                        selectedRecord?.monthSubmit,
                        { employeeId: selectedRecord?.employeeId }
                    );
                }
                getTimesheetAllList({ page: 0, size: 10 });
            }
        },
        [dispatch, currentCompany, currentEmployee, selectedRecord, getTimesheetAllList, getTimesheetDetails]
    );

    const onApproveTimesheetByList = useCallback(
        (selectedRecords) => {
            return new Promise((resolve) => {

                Modal.confirm({
                    title: t("dialog.confirmation.header"),
                    content: (
                        <span>
                            {i18n.language == "en" ? `Are you sure you want to confirm the timesheet approval ?`
                                : `คุณแน่ใจหรือไม่ที่จะยืนยันการอนุมัติไทม์ชีต`}
                        </span>
                    ),
                    onOk() {
                        return approveTimesheetByList(selectedRecords).then(resolve);
                    },
                    okText: t('common.confirm'),
                    cancelText: t('common.cancel'),
                    okButtonProps: { style: { height: "40px", borderRadius: "8px", backgroundColor: "var(--button-color)", boxShadow: "none" } },
                    cancelButtonProps: { style: { height: "40px", borderRadius: "8px", boxShadow: "none" } },
                });
            });
        },
        [approveTimesheetByList, i18n.language, t]
    );

    const onRejectTimesheetByList = useCallback(
        (selectedRecords) => {
            return new Promise((resolve) => {

                Modal.confirm({
                    title: t("dialog.confirmation.header"),
                    content: (
                        <span>
                            {i18n.language == "en" ? `Are you sure you want to confirm disapproval of timesheet ?`
                                : `คุณแน่ใจหรือไม่ที่จะยืนยันการไม่อนุมัติไทม์ชีต`}
                        </span>
                    ),
                    onOk() {
                        return rejectTimesheetByList(selectedRecords).then(resolve);
                    },
                    okText: t('common.confirm'),
                    cancelText: t('common.cancel'),
                    okButtonProps: { style: { height: "40px", borderRadius: "8px", backgroundColor: "var(--button-color)", boxShadow: "none" } },
                    cancelButtonProps: { style: { height: "40px", borderRadius: "8px", boxShadow: "none" } },
                });
            });
        },
        [rejectTimesheetByList, i18n.language, t]
    );

    const getProjectDropdownForApprover = useCallback(async () => {
        if (typeof currentCompany !== "object") {
            try {
                dispatch(setIsLoading(true));
                const values = {
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    employeeId: currentEmployee
                };
                const response = await service.dropdownProjectForApprover(values);
                setProjectForApprover(response?.data?.content);
            } catch (error) {
            } finally {
                dispatch(setIsLoading(false));
            }
        }
    }, [currentCompany, currentEmployee, dispatch]);


    useEffect(() => {
        if (currentCompany) {
            getProjectDropdownForApprover()
        }
    }, [currentCompany])

    const modifiedProjectForApproverOption = useMemo(() => {
        const modifiedOptions = projectForApprover?.map((opt) => ({
            ...opt,
            value: opt?.projectId,
            label: i18n.language === "en" ? opt?.projectNameEn : opt?.projectName,
        }));

        return modifiedOptions;
    }, [i18n.language, projectForApprover]);

    const modifiedTaskOption = useMemo(() => {
        const modifiedOptions = taskDropdown?.map((opt) => ({
            ...opt,
            value: opt?.id,
            label: i18n.language === "en" ? opt?.taskNameEn : opt?.taskName,
        }));

        return modifiedOptions;
    }, [i18n.language, taskDropdown]);

    const handleSearch = (values) => {
        setSearch({})

        try {
            setSearch({
                ...values,
            })

            getTimesheetAllList({
                ...values,
                month: values?.month?.format("MM"),
                page: 0, size: 10
            })
        } catch (error) { }
    };

    const handleSearchDetailModal = (values) => {
        setSearchDetail({})

        try {
            const filters = {
                ...values,
                startDate: values?.startToEndDate?.[0]
                    ? values?.startToEndDate?.[0]?.format('YYYY-MM-DD')
                    : null,
                endDate: values?.startToEndDate?.[1]
                    ? values?.startToEndDate?.[1]?.format('YYYY-MM-DD')
                    : null,
                employeeId: selectedRecord?.employeeId,
            };
            setSearchDetail(filters);
            getTimesheetDetails(
                { page: 0, size: 10 },
                selectedRecord?.stpTimeSheetId,
                selectedRecord?.monthSubmit,
                filters
            );
        } catch (error) { }
    };

    useEffect(() => {
        if (!myTimesheetAllList) {
            setMyTimesheetAllListWithKey([]);
            return;
        }

        setMyTimesheetAllListWithKey(
            myTimesheetAllList.map((item) => ({
                ...item,
                rowKey: generateRandomString(),
            }))
        );
    }, [myTimesheetAllList, currentCompany]);

    return {
        isLoading,
        useFilter,
        useFilterDetail,
        pagination,
        paginationDetails,
        myTimesheetAllListWithKey,
        setUseFilter,
        setUseFilterDetail,
        onPageChange,
        rowSelection,
        hasSelected,
        selectedRowKeys,
        setSelectedRowKeys,
        onApproveTimesheetByEmployee,
        onRejectedTimesheetByEmployee,
        onView,
        isModalOpen,
        timesheetDetailsByEmp,
        selectedRecord,
        onTimesheetDetailsPageChange,
        setIsModalOpen,
        onApproveTimesheetByList,
        onRejectTimesheetByList,
        projectForApproverOptions: modifiedProjectForApproverOption,
        taskOptions: modifiedTaskOption,
        handleSearch,
        handleSearchDetailModal
    };
}
export default useMyTeam;